if (!customElements.get('promotional-popup')) {
  customElements.define('promotional-popup', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.dialog = this.querySelector('[data-promotional-popup-dialog]');
      this.panel = this.querySelector('[data-promotional-popup-panel]');
      this.mediaImage = this.querySelector('.promotional-popup__image');
      this.copyButton = this.querySelector('[data-promotional-popup-code]');
      this.copyStatus = this.querySelector('[data-promotional-popup-copy-status]');
      this.position = this.dataset.position || 'bottom-right';
      this.reducedMotion = window.ThemeComponents?.motion?.reduced || matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.stagedMedia = this.dataset.stagedMedia === 'true' && Boolean(this.querySelector('.promotional-popup__media'));
      this.storageKey = `theme-promotional-popup:${this.dataset.sectionId}`;
      const { signal } = this.abortController;

      this.querySelectorAll('[data-promotional-popup-close]').forEach(button => {
        button.addEventListener('click', () => this.close(), { signal });
      });
      this.dialog?.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, { signal });
      this.dialog?.addEventListener('keydown', event => {
        if (this.position === 'center' && event.key === 'Tab') window.ThemeComponents?.focus?.trapTab(event, this.dialog);
      }, { signal });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.dialog?.open) this.close();
      }, { signal });
      this.copyButton?.addEventListener('click', () => this.copyCode(), { signal });

      const designMode = this.dataset.designMode === 'true';
      const formSuccess = this.dataset.formSuccess === 'true';
      if (designMode || formSuccess || this.shouldDisplay()) {
        const delay = designMode || formSuccess ? 0 : Number(this.dataset.delay || 0) * 1000;
        this.openTimer = window.setTimeout(() => this.openWhenAvailable(), delay);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.mediaLoadAbortController?.abort();
      clearTimeout(this.openTimer);
      clearTimeout(this.retryTimer);
      clearTimeout(this.copyResetTimer);
      clearTimeout(this.mediaRevealTimer);
      clearTimeout(this.mediaLoadFallbackTimer);
    }

    shouldDisplay() {
      const frequency = this.dataset.frequency || 'session';
      if (frequency === 'always') return true;

      try {
        if (frequency === 'session') return sessionStorage.getItem(this.storageKey) !== 'dismissed';
        const dismissedAt = Number(localStorage.getItem(this.storageKey) || 0);
        const interval = Number(frequency) * 24 * 60 * 60 * 1000;
        return !dismissedAt || Date.now() - dismissedAt >= interval;
      } catch (_error) {
        return true;
      }
    }

    rememberDismissal() {
      const frequency = this.dataset.frequency || 'session';
      if (frequency === 'always') return;

      try {
        if (frequency === 'session') {
          sessionStorage.setItem(this.storageKey, 'dismissed');
        } else {
          localStorage.setItem(this.storageKey, String(Date.now()));
        }
      } catch (_error) {}
    }

    openWhenAvailable(attempt = 0) {
      if (!this.dialog || this.dialog.open) return;
      const anotherDialog = [...document.querySelectorAll('dialog[open]')].some(dialog => dialog !== this.dialog);
      if (anotherDialog) {
        if (attempt < 30) this.retryTimer = window.setTimeout(() => this.openWhenAvailable(attempt + 1), 1000);
        return;
      }
      this.open();
    }

    open() {
      if (!this.dialog || this.dialog.open) return;
      this.returnFocus = document.activeElement;
      clearTimeout(this.mediaRevealTimer);
      clearTimeout(this.mediaLoadFallbackTimer);
      this.mediaLoadAbortController?.abort();
      this.mediaLoadAbortController = new AbortController();
      this.dialog.classList.remove('is-media-revealed');
      if (this.stagedMedia && this.mediaImage) {
        this.mediaImage.loading = 'eager';
        this.mediaImage.fetchPriority = 'high';
      }

      try {
        if (this.position === 'center') {
          this.dialog.showModal();
        } else {
          this.dialog.show();
        }
      } catch (_error) {
        this.dialog.setAttribute('open', '');
      }

      requestAnimationFrame(() => {
        this.dialog.classList.add('is-open');
        if (this.stagedMedia && !this.reducedMotion) {
          this.mediaRevealTimer = window.setTimeout(() => this.revealMediaWhenReady(), 360);
        } else {
          this.dialog.classList.add('is-media-revealed');
        }
        if (this.position === 'center') this.querySelector('.promotional-popup__close')?.focus({ preventScroll: true });
      });
    }

    revealMediaWhenReady() {
      const reveal = () => {
        clearTimeout(this.mediaLoadFallbackTimer);
        this.dialog?.classList.add('is-media-revealed');
      };
      if (!this.mediaImage || this.mediaImage.complete) {
        reveal();
        return;
      }

      this.mediaImage.addEventListener('load', reveal, { once: true, signal: this.mediaLoadAbortController.signal });
      this.mediaImage.addEventListener('error', reveal, { once: true, signal: this.mediaLoadAbortController.signal });
      this.mediaLoadFallbackTimer = window.setTimeout(reveal, 500);
    }

    async close() {
      if (!this.dialog?.open || this.closing) return;
      this.closing = true;
      clearTimeout(this.mediaRevealTimer);
      clearTimeout(this.mediaLoadFallbackTimer);
      this.mediaLoadAbortController?.abort();
      this.dialog.classList.remove('is-open');

      if (!window.ThemeComponents?.motion?.reduced) {
        const animations = this.dialog.getAnimations({ subtree: true }).map(animation => animation.finished.catch(() => {}));
        await Promise.all(animations);
      }

      if (this.dialog.open) this.dialog.close();
      this.dialog.classList.remove('is-media-revealed');
      this.rememberDismissal();
      if (this.position === 'center') window.ThemeComponents?.focus?.restore(this.returnFocus);
      this.closing = false;
    }

    async copyCode() {
      const code = this.copyButton?.dataset.code;
      if (!code) return;

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const input = document.createElement('textarea');
          input.value = code;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.append(input);
          input.select();
          const copied = document.execCommand('copy');
          input.remove();
          if (!copied) throw new Error('Copy command failed');
        }
        this.updateCopyStatus(this.copyButton.dataset.copiedLabel);
      } catch (_error) {
        this.updateCopyStatus(this.copyButton.dataset.errorLabel);
      }
    }

    updateCopyStatus(message) {
      if (this.copyStatus) this.copyStatus.textContent = message || '';
      this.copyButton?.classList.add('is-copied');
      clearTimeout(this.copyResetTimer);
      this.copyResetTimer = window.setTimeout(() => this.copyButton?.classList.remove('is-copied'), 1600);
    }
  });
}
